import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiArrowRight,
  FiMic,
  FiBarChart2,
  FiLock,
  FiCheckCircle,
  FiClock,
  FiAward,
  FiAlertCircle,
  FiTarget,
  FiZap,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiStar,
} from "react-icons/fi";
import api from "../services/api";
import { subscriptionService } from "../services";

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const [interviews, setInterviews] = useState([]);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (token) {
      fetchInterviews();
      fetchSubscription();
    }
  }, [token]);

  const fetchSubscription = async () => {
    try {
      const response = await subscriptionService.getSubscription();
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  const fetchInterviews = async () => {
    try {
      const response = await api.get("/interviews");
      // Only show completed interviews
      const completedInterviews = response.data.interviews.filter(
        (interview) => interview.status === "completed"
      );
      setInterviews(completedInterviews);
    } catch (error) {
      console.error("Error fetching interviews:", error);
    }
  };

  const handleViewResults = (interviewId) => {
    navigate(`/interviews/${interviewId}/results`);
  };

  return (
    <div className="home-redesign">
      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-content">
          <p className="home-hero-subtitle">{t("home.badge")}</p>
          <h1 className="home-hero-title">{t("home.heroTitle")}</h1>
          <p className="home-hero-description">{t("home.heroDescription")}</p>
          <div className="home-hero-buttons">
            {!token ? (
              <button
                onClick={() => navigate("/register")}
                className="home-hero-btn home-hero-btn-primary"
              >
                {t("home.getStartedFree")}
                <FiArrowRight />
              </button>
            ) : (
              <button
                onClick={() => navigate("/interviews/create")}
                className="home-hero-btn home-hero-btn-primary"
              >
                {t("interview.newInterview")}
                <FiArrowRight />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Subscription Cancellation Warning */}
      {token &&
        subscription?.status === "cancelled" &&
        subscription?.daysRemaining > 0 && (
          <div
            style={{
              maxWidth: "1200px",
              margin: "2rem auto",
              padding: "0 2rem",
            }}
          >
            <div className="alert alert-warning">
              <FiAlertCircle className="alert-icon" />
              <div className="alert-content">
                <p className="alert-title">
                  Tu suscripción Premium fue cancelada
                </p>
                <p className="alert-message">
                  Seguirás disfrutando de Premium hasta el{" "}
                  <strong>
                    {new Date(subscription.finalEndDate).toLocaleDateString(
                      "es-ES"
                    )}
                  </strong>
                  . Después, tu cuenta volverá al plan gratuito.
                </p>
                <p className="alert-message" style={{ marginTop: "0.5rem" }}>
                  <strong>Días restantes: {subscription.daysRemaining}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Features Section */}
      <section className="home-section">
        <h2 className="home-section-title">{t("home.whyChoose")}</h2>
        <p className="home-section-subtitle">{t("home.whyChooseDesc")}</p>
        <div className="home-features-grid">
          <div className="home-feature-card">
            <div className="home-feature-icon-wrapper">
              <FiMic />
            </div>
            <h3 className="home-feature-title">{t("home.voiceInterviews")}</h3>
            <p className="home-feature-description">
              {t("home.voiceInterviewsDesc")}
            </p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon-wrapper">
              <FiBarChart2 />
            </div>
            <h3 className="home-feature-title">
              {t("home.analyticsTracking")}
            </h3>
            <p className="home-feature-description">
              {t("home.analyticsTrackingDesc")}
            </p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon-wrapper">
              <FiLock />
            </div>
            <h3 className="home-feature-title">{t("home.securePrivate")}</h3>
            <p className="home-feature-description">
              {t("home.securePrivateDesc")}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="home-stats">
        <div className="home-stats-grid">
          <div className="home-stat-item">
            <span className="home-stat-number">650+</span>
            <span className="home-stat-label">Usuarios Satisfechos</span>
          </div>
          <div className="home-stat-item">
            <span className="home-stat-number">500+</span>
            <span className="home-stat-label">Entrevistas Completadas</span>
          </div>
          <div className="home-stat-item">
            <span className="home-stat-number">95%</span>
            <span className="home-stat-label">Tasa de Éxito</span>
          </div>
          <div className="home-stat-item">
            <span className="home-stat-number">24/7</span>
            <span className="home-stat-label">Soporte Disponible</span>
          </div>
        </div>
      </section>

      {/* Completed Interviews Section - Only if logged in */}
      {token && interviews.length > 0 && (
        <section className="home-section">
          <h2 className="home-section-title">{t("home.recentInterviews")}</h2>
          <p className="home-section-subtitle">{t("home.reviewCompleted")}</p>
          <div className="home-features-grid">
            {interviews.slice(0, 3).map((interview) => (
              <div key={interview._id} className="home-feature-card">
                <div className="home-feature-icon-wrapper">
                  <FiCheckCircle />
                </div>
                <h3 className="home-feature-title">
                  {interview.title ||
                    `${t("interview.newInterview")} ${interview._id.slice(-6)}`}
                </h3>
                <div style={{ marginBottom: "1rem" }}>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <FiClock />
                    {new Date(interview.createdAt).toLocaleDateString()}
                  </p>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                    }}
                  >
                    <FiAward />
                    {interview.responses?.length || 0} {t("home.questions")}
                  </p>
                </div>
                <button
                  onClick={() => handleViewResults(interview._id)}
                  className="home-hero-btn home-hero-btn-primary"
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {t("home.viewResults")} <FiArrowRight />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Services Section */}
      <section className="home-section">
        <h2 className="home-section-title">Nuestros Servicios</h2>
        <p className="home-section-subtitle">
          Descubre todas las funcionalidades que ofrecemos para mejorar tu
          preparación
        </p>
        <div className="home-services-grid">
          <div className="home-service-card">
            <div className="home-feature-icon-wrapper">
              <FiTarget />
            </div>
            <h3 className="home-service-title">Entrevistas Personalizadas</h3>
            <p className="home-service-description">
              Crea entrevistas adaptadas a tu perfil y objetivos profesionales
            </p>
          </div>
          <div className="home-service-card">
            <div className="home-feature-icon-wrapper">
              <FiZap />
            </div>
            <h3 className="home-service-title">Respuestas en Tiempo Real</h3>
            <p className="home-service-description">
              Obtén feedback inmediato sobre tus respuestas y mejora
              continuamente
            </p>
          </div>
          <div className="home-service-card">
            <div className="home-feature-icon-wrapper">
              <FiShield />
            </div>
            <h3 className="home-service-title">Privacidad Garantizada</h3>
            <p className="home-service-description">
              Tus datos están protegidos con los más altos estándares de
              seguridad
            </p>
          </div>
          <div className="home-service-card">
            <div className="home-feature-icon-wrapper">
              <FiTrendingUp />
            </div>
            <h3 className="home-service-title">Análisis Avanzado</h3>
            <p className="home-service-description">
              Visualiza tu progreso con gráficos detallados y métricas precisas
            </p>
          </div>
          <div className="home-service-card">
            <div className="home-feature-icon-wrapper">
              <FiUsers />
            </div>
            <h3 className="home-service-title">Comunidad Activa</h3>
            <p className="home-service-description">
              Únete a miles de usuarios que mejoran sus habilidades diariamente
            </p>
          </div>
          <div className="home-service-card">
            <div className="home-feature-icon-wrapper">
              <FiStar />
            </div>
            <h3 className="home-service-title">Contenido Premium</h3>
            <p className="home-service-description">
              Accede a entrevistas ilimitadas y funciones exclusivas
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="home-section home-about">
        <h2 className="home-section-title">Nuestros Valores</h2>
        <p className="home-section-subtitle">
          Los principios que guían nuestro trabajo y compromiso contigo
        </p>
        <div className="home-values-grid">
          <div className="home-value-card">
            <h3 className="home-value-title">Excelencia</h3>
            <p className="home-value-description">
              Nos comprometemos a ofrecer la mejor experiencia posible,
              mejorando constantemente nuestra plataforma y servicios.
            </p>
          </div>
          <div className="home-value-card">
            <h3 className="home-value-title">Innovación</h3>
            <p className="home-value-description">
              Utilizamos las últimas tecnologías de IA para proporcionarte una
              experiencia de entrevista realista y efectiva.
            </p>
          </div>
          <div className="home-value-card">
            <h3 className="home-value-title">Compromiso</h3>
            <p className="home-value-description">
              Estamos dedicados a tu éxito profesional y trabajamos para que
              alcances tus objetivos de carrera.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta">
        <div className="home-cta-content">
          <h2 className="home-cta-title">{t("home.ctaTitle")}</h2>
          <p className="home-cta-description">{t("home.ctaDescription")}</p>
          {!token ? (
            <button
              onClick={() => navigate("/login")}
              className="home-hero-btn home-hero-btn-primary"
            >
              {t("home.ctaButton")} <FiArrowRight />
            </button>
          ) : (
            <button
              onClick={() => navigate("/subscription")}
              className="home-hero-btn home-hero-btn-primary"
            >
              {t("subscription.title")} <FiArrowRight />
            </button>
          )}
        </div>
      </section>

      {/* Modern Footer */}
      <footer className="modern-footer">
        <div className="modern-footer-container">
          {/* Footer Top */}
          <div className="modern-footer-top">
            {/* Company Info */}
            <div className="modern-footer-column">
              <h3 className="modern-footer-logo">{t("common.appName")}</h3>
              <p className="modern-footer-description">
                La plataforma líder en preparación de entrevistas con IA. Mejora
                tus habilidades y consigue el trabajo de tus sueños.
              </p>
              <div className="modern-footer-social">
                <a
                  href="#"
                  className="modern-footer-social-link"
                  aria-label="Twitter"
                >
                  <svg
                    width="20"
                    height="20"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="modern-footer-social-link"
                  aria-label="LinkedIn"
                >
                  <svg
                    width="20"
                    height="20"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="modern-footer-social-link"
                  aria-label="GitHub"
                >
                  <svg
                    width="20"
                    height="20"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="modern-footer-social-link"
                  aria-label="Instagram"
                >
                  <svg
                    width="20"
                    height="20"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div className="modern-footer-column">
              <h4 className="modern-footer-heading">Producto</h4>
              <ul className="modern-footer-links">
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/interviews");
                    }}
                  >
                    Entrevistas
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/subscription");
                    }}
                  >
                    Precios
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/dashboard");
                    }}
                  >
                    Dashboard
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/settings");
                    }}
                  >
                    Configuración
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div className="modern-footer-column">
              <h4 className="modern-footer-heading">Compañía</h4>
              <ul className="modern-footer-links">
                <li>
                  <a href="#">Sobre Nosotros</a>
                </li>
                <li>
                  <a href="#">Blog</a>
                </li>
                <li>
                  <a href="#">Carreras</a>
                </li>
                <li>
                  <a href="#">Contacto</a>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div className="modern-footer-column">
              <h4 className="modern-footer-heading">Legal</h4>
              <ul className="modern-footer-links">
                <li>
                  <a href="#">Privacidad</a>
                </li>
                <li>
                  <a href="#">Términos</a>
                </li>
                <li>
                  <a href="#">Cookies</a>
                </li>
                <li>
                  <a href="#">Licencias</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="modern-footer-bottom">
            <p className="modern-footer-copyright">
              © {new Date().getFullYear()} {t("common.appName")}. Todos los
              derechos reservados.
            </p>
            <div className="modern-footer-bottom-links">
              <a href="#">Política de Privacidad</a>
              <span className="modern-footer-divider">•</span>
              <a href="#">Términos de Servicio</a>
              <span className="modern-footer-divider">•</span>
              <a href="#">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
